import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Loader2 } from "lucide-react";
import { UnsavedChangesDialog } from "@/components/admin/locations/components/UnsavedChangesDialog";
import { Checkbox } from "@/components/ui/checkbox";

interface BigFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  title: string;
  children: React.ReactNode;
  saveLabel?: string;
  isSaving?: boolean;
  hideActions?: boolean;
  description?: React.ReactNode;
  isDirty?: boolean;
  extraActions?: React.ReactNode;
  pendingAction?: 'delete' | null;
  cancelLabel?: string;
  showAddAnother?: boolean;
  addAnotherChecked?: boolean;
  onAddAnotherChange?: (checked: boolean) => void;
  showSaveButton?: boolean;
  showCancelButton?: boolean;
  saveDisabled?: boolean;
  footerTop?: React.ReactNode;
  /** Right-aligned actions in footer (e.g. "Enter with JSON" toggle). */
  rightFooterActions?: React.ReactNode;
  /** Optional className for DialogContent (e.g. to override width: sm:max-w-[90vw]). */
  contentClassName?: string;
}

export const BigFormModal = ({
  isOpen,
  onClose,
  onSave,
  title,
  children,
  saveLabel = "Save",
  isSaving = false,
  hideActions = false,
  description,
  isDirty = false,
  extraActions,
  pendingAction = null,
  cancelLabel = "Cancel",
  showAddAnother = false,
  addAnotherChecked = false,
  onAddAnotherChange,
  showSaveButton = true,
  showCancelButton = true,
  saveDisabled = false,
  footerTop,
  rightFooterActions,
  contentClassName,
}: BigFormModalProps) => {
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  
  // Reset the unsaved dialog state whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setShowUnsavedDialog(false);
    }
  }, [isOpen]);

  const handleCloseAttempt = () => {
    console.log("Close attempt detected", {
      isDirty,
      pendingAction
    });
    
    // If we're in the middle of a delete operation, or form is not dirty, close directly
    if (pendingAction === 'delete' || !isDirty) {
      console.log("Form is not dirty or delete operation in progress, closing directly");
      onClose();
    } else {
      console.log("Form is dirty, showing confirmation dialog");
      setShowUnsavedDialog(true);
    }
  };

  const handleDiscardChanges = () => {
    console.log("User confirmed discard changes");
    setShowUnsavedDialog(false);
    onClose();
  };

  const handleSaveChanges = () => {
    console.log("User selected to save changes");
    setShowUnsavedDialog(false);
    if (onSave) {
      onSave();
    } else {
      onClose();
    }
  };

  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          if (!open) {
            console.log("Dialog closing via Escape key or outside click");
            handleCloseAttempt();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className={cn("sm:max-w-[700px] lg:max-w-[910px] h-[90vh] flex flex-col p-0 gap-0", contentClassName)}
        >
          <DialogHeader className="p-6 border-b border-gray-200 shadow-sm flex-shrink-0 z-10">
            <DialogTitle className="text-xl font-semibold">
              {title}
            </DialogTitle>
            {description && (
              <div className="space-y-2 mb-4 text-base text-muted-foreground text-sm">
                {description}
              </div>
            )}
            <button
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
              onClick={handleCloseAttempt}
              type="button"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </button>
          </DialogHeader>
          
          {/* Main content area with ScrollArea */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-6 py-6 pb-12">
                {children}
              </div>
            </ScrollArea>
          </div>
          
          {!hideActions && (
            <DialogFooter className="w-full flex flex-col sm:flex-col p-6 border-t gap-3">
              {footerTop && (
                <div className="w-full -mt-1">
                  {footerTop}
                </div>
              )}
              {/* Bottom row - extra actions left, buttons right */}
              <div className="w-full flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {extraActions}
                </div>
                {/* Right side - main action buttons */}
                <div className="flex flex-col items-end gap-3">
                {/* Button row */}
                <div className="flex items-center gap-2">
                  {rightFooterActions}
                  {showCancelButton && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCloseAttempt}
                      disabled={isSaving}
                    >
                      {cancelLabel}
                    </Button>
                  )}
                  {showSaveButton && (
                    <Button
                      type="button"
                      onClick={onSave}
                      disabled={isSaving || saveDisabled}
                      className="min-w-[100px]"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        saveLabel
                      )}
                    </Button>
                  )}
                </div>
              </div>
              {/* Add another checkbox row */}
              {showAddAnother && (
                <div className="flex items-center space-x-2 pr-4 pb-2">
                  <Checkbox
                    id="add-another"
                    checked={addAnotherChecked}
                    onCheckedChange={(checked) => onAddAnotherChange?.(checked === true)}
                  />
                  <label
                    htmlFor="add-another"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Add another like this
                  </label>
                </div>
              )}
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onOpenChange={setShowUnsavedDialog}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveChanges}
        pendingAction={pendingAction}
      />
    </>
  );
};
